import React, { FC, useLayoutEffect } from "react";
import { observer } from "mobx-react";
import {
  AddressModel,
  CheckoutPaymentPageModel,
  CheckOutPayPageFormFields,
  CountryWithStateModel,
  CustomerFacingCheckOutItemModel,
  getPurchaseItemsPriceDetails,
  MerchantAccountWithPaySettingsModel,
  PurchaseCheckOutAddOnDetails,
  PurchaseItemsPriceModel,
  PurchaseMerchantAccountSettingsModel,
  PurchasePaymentModel,
  SellerModel,
  StateModel,
} from "../models";
import { ICheckOutCombinedColors } from "../interfaces";
import {
  CheckOutButtonStyle,
  CheckOutPayButtonCaption,
  CheckOutPayFields,
  CheckOutPromoCodeSelectionType,
} from "../enums";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
  ModalComponent,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { AccountSettingsStore } from "../store";
import CountryPhoneDropdown from "./CountryPhoneDropdown";
import AddressFormComponent from "./AddressFormComponent";
import { sanitizeHtml } from "../utils/sanitizeHtml";

type IProps = {
  payDetails: CheckoutPaymentPageModel;
  paymentSectionData: PurchasePaymentModel;
  handleDataChange: (field: keyof PurchasePaymentModel, value: any) => void;
  countriesWithState: CountryWithStateModel[];
  onPayClick: () => void;
  color?: ICheckOutCombinedColors;
  merchantAccount: MerchantAccountWithPaySettingsModel;
  addons: PurchaseCheckOutAddOnDetails[];
  items: CustomerFacingCheckOutItemModel[];
  action?: string;
  hasMultiplePaymentMethods?: boolean;
  isPaymentInProgress?: boolean;
  seller: SellerModel;
  showSurchargeCheckbox?: boolean;
  includeSurcharge?: boolean;
  surchargePercentage?: number;
  isDonation?: boolean;
  promoCodeSelectionType?: keyof typeof CheckOutPromoCodeSelectionType;
};

type SellerPolicyKey = "terms" | "returnPolicy";

/* Exactly one of the two is populated: the editor HTML when the seller
   authored the policy in the rich-text editor, the plain text otherwise. */
type SellerPolicyCopy = { html: string; text: string };

/* Everything the checkbox, its "View ..." link and its popup need. The seller's
   Terms & Conditions and Return Policy render identically, so they are
   described as data and rendered by one function. */
type SellerPolicy = {
  key: SellerPolicyKey;
  inputId: string;
  field: keyof PurchasePaymentModel;
  /** Popup header. */
  title: string;
  /** Checkbox caption. */
  label: string;
  linkCaption: string;
  errorMessage: string;
  /** The checkout is set to anything other than "Do Not Display". */
  displayed: boolean;
  /** Copy shown under the checkbox, above the "View ..." link. */
  description?: React.ReactNode;
  copy: SellerPolicyCopy | null;
};

const PurchasePaymentFormInputs: FC<IProps> = ({
  payDetails,
  handleDataChange,
  paymentSectionData,
  countriesWithState,
  onPayClick,
  color,
  merchantAccount,
  items,
  addons,
  action,
  hasMultiplePaymentMethods,
  isPaymentInProgress,
  seller,
  showSurchargeCheckbox,
  includeSurcharge,
  surchargePercentage,
  isDonation,
  promoCodeSelectionType,
}) => {
  const billingAddressRef = React.useRef<HTMLDivElement>(null);
  const shippingAddressRef = React.useRef<HTMLDivElement>(null);
  const payButtonRef = React.useRef<HTMLButtonElement>(null);
  const formInputsRef = React.useRef<HTMLDivElement>(null);
  const { formData } = paymentSectionData;
  const { formFields } = payDetails.formData;

  /* Which seller policy is open in the read-only popup, if any. */
  const [openPolicyKey, setOpenPolicyKey] =
    React.useState<SellerPolicyKey | null>(null);

  useLayoutEffect(() => {
    if (!action) return;
    if (action === "isBillingAddressChecked") {
      if (
        payDetails.formData.isBillingAddressChecked &&
        billingAddressRef.current
      ) {
        billingAddressRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (action === "isShippingAddressChecked") {
      if (
        payDetails.formData.isShippingAddressChecked &&
        shippingAddressRef.current
      ) {
        shippingAddressRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    if (action === "payButtonType") {
      payButtonRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    if (action === "formFields") {
      formInputsRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [action]);

  const handleFormInputChange = (field: string, value: string) => {
    handleDataChange("formData", { ...formData, [field]: value });
  };

  const handleSelect = (id: string | null) => {
    if (id) {
      const country = countriesWithState.find((country) => country.id === id);
      if (country) {
        handleDataChange("countryDetails", country);
      }
    }
  };

  const renderField = (field: CheckOutPayPageFormFields) => {
    let placeholder = field.field;

    switch (field.field) {
      case CheckOutPayFields.FirstLastName:
        return (
          <>
            <FlexContainer
              alignItems="center"
              spacing={10}
              key={field.field}
              ref={formInputsRef}
            >
              <Input
                type="text"
                id="firstName"
                value={formData["firstName"] || ""}
                onChange={(e) =>
                  handleFormInputChange("firstName", e.target.value)
                }
                placeholder="First Name"
                error={paymentSectionData.hasError(
                  "firstName" as keyof PurchasePaymentModel,
                )}
                errorMessage="*Please enter first and last name"
              />
              <Input
                type="text"
                id="lastName"
                value={formData["lastName"] || ""}
                onChange={(e) =>
                  handleFormInputChange("lastName", e.target.value)
                }
                placeholder="Last Name"
                error={paymentSectionData.hasError(
                  "lastName" as keyof PurchasePaymentModel,
                )}
                errorMessage="*Please enter first and last name"
              />
            </FlexContainer>
          </>
        );
      case CheckOutPayFields.MobileNumber:
        return (
          <FlexContainer fxLayout="column">
            <CountryPhoneDropdown
              key={field.field}
              selectedCountry={paymentSectionData.countryDetails}
              error={paymentSectionData.hasError(
                "MobileNumber" as keyof PurchasePaymentModel,
              )}
              errorMessage={"*Please enter a valid mobile number"}
              onCountryChange={(countryId: string) => handleSelect(countryId)}
              phoneNumber={formData[field.field] || ""}
              onPhoneNumberChange={(value: string) =>
                handleFormInputChange(field.field, value)
              }
              availableCountries={countriesWithState}
              className="purchase-phone-dropdown"
            />
          </FlexContainer>
        );
      case CheckOutPayFields.EmailAddress:
        placeholder = "Email";
        break;
      default:
        placeholder = field.field === "TaxId" ? "Tax Id" : field.field;
    }

    return (
      <FlexContainer key={field.field} fxLayout="column">
        <Input
          type="text"
          id={field.field}
          value={formData[field.field] || ""}
          onChange={(e) => handleFormInputChange(field.field, e.target.value)}
          placeholder={placeholder}
          error={paymentSectionData.hasError(
            field.field as keyof PurchasePaymentModel,
          )}
          errorMessage="*Please enter a valid value"
        />
      </FlexContainer>
    );
  };

  // Single source of truth for the checkout price breakdown. Reused for the
  // order total, the surcharge amount shown in the Terms & Conditions area and
  // anywhere else that needs the calculated line items, so we never duplicate
  // the promo-code / surcharge math.
  const getPriceDetails = (
    surchargePercentage?: number,
  ): PurchaseItemsPriceModel[] => {
    const clonedItems = items.map(
      (item) =>
        new CustomerFacingCheckOutItemModel({
          ...item,
          selectedVariantIndex:
            item.selectedVariantIndex === -1 ? 0 : item.selectedVariantIndex,
        }),
    );

    const purchaseDetailsToApplyPromoCode: PurchaseItemsPriceModel[] =
      getPurchaseItemsPriceDetails(clonedItems, addons, 0, undefined);

    const totalPriceToApplyPromoCode = purchaseDetailsToApplyPromoCode
      .filter((priceDetail) => priceDetail.dueLater !== true)
      .reduce((total, priceDetail) => total + priceDetail.totalPrice, 0);

    const minimumPurchase = paymentSectionData.promotionRelatedData
      ? paymentSectionData.promotionRelatedData.minimumPurchase || 0
      : 0;

    const promoData =
      promoCodeSelectionType === "ApplyCode"
        ? totalPriceToApplyPromoCode > minimumPurchase
          ? paymentSectionData.promotionRelatedData
          : undefined
        : paymentSectionData.promotionRelatedData;

    return getPurchaseItemsPriceDetails(
      clonedItems,
      addons,
      surchargePercentage,
      promoData || undefined,
    );
  };

  const sumPriceDetails = (
    priceDetails: PurchaseItemsPriceModel[],
    selector: (priceDetail: PurchaseItemsPriceModel) => number,
  ): number =>
    priceDetails
      .filter((priceDetail) => priceDetail.dueLater !== true)
      .reduce((total, priceDetail) => total + selector(priceDetail), 0);

  const includeSurchargeOnApplyPromo = paymentSectionData.promotionRelatedData
    ? !paymentSectionData.promotionRelatedData.waiveSurcharge
    : true;

  // Always the checkout/link's own configured percentage - the same value the server charges.
  // Never the merchant/payment-method surcharge, which this feature replaced.
  const surchargePercentageValue = surchargePercentage ?? 0;

  // Surcharge amount that would apply for the current items regardless of
  // whether the customer has opted in yet (needed so "Ask Permission" can show
  // the amount before the box is checked). Zero when the amount is not yet
  // known (e.g. a builder preview with no concrete price).
  const potentialSurchargeAmount = sumPriceDetails(
    getPriceDetails(
      includeSurchargeOnApplyPromo ? surchargePercentageValue : 0,
    ),
    (priceDetail) => priceDetail.surcharge || 0,
  );

  const isSurchargeAmountKnown = potentialSurchargeAmount > 0;

  // Order total. Surcharge is only folded in when it is actually being applied
  // (Include Surcharge, or Ask Permission with the box checked).
  const totalPrice = sumPriceDetails(
    getPriceDetails(
      includeSurcharge && includeSurchargeOnApplyPromo
        ? surchargePercentageValue
        : 0,
    ),
    (priceDetail) => priceDetail.totalPrice,
  );

  const price = AccountSettingsStore.formatAmount(
    Math.round(totalPrice * 100) / 100,
    true,
  );

  // Percentages render without trailing zeros: 3%, 2.9%, 2.95%.
  const surchargePercentageLabel = `${parseFloat(
    surchargePercentageValue.toFixed(2),
  )}`;
  const surchargeAmountLabel = AccountSettingsStore.formatAmount(
    potentialSurchargeAmount,
    true,
  );
  const merchantName = seller?.name || "";

  // Consent text shown beside the "Cover Processing Fees" checkbox
  // (Ask Permission). Percentage, amount and merchant name are all dynamic and
  // rendered in bold, matching how the merchant name is emphasised in the
  // Terms & Conditions text.
  const buildSurchargeConsentText = (): React.ReactNode => {
    const amountNode = isSurchargeAmountKnown ? (
      <>
        {" "}
        totaling <strong>${surchargeAmountLabel}</strong>
      </>
    ) : null;

    if (isDonation) {
      return (
        <>
          I agree to pay a <strong>{surchargePercentageLabel}%</strong>{" "}
          surcharge
          {amountNode} so more of my donation goes toward{" "}
          {merchantName ? <strong>{merchantName}</strong> : "the cause"}.
        </>
      );
    }

    return (
      <>
        I agree to pay a <strong>{surchargePercentageLabel}%</strong> processing
        fee{amountNode}.
      </>
    );
  };

  // Value shown in the automatic surcharge message (Include Surcharge). In the
  // builder preview the transaction amount is not available, so we fall back to
  // the percentage; in the customer-facing checkout the calculated dollar
  // amount is shown instead.
  const surchargeIncludedValue = isSurchargeAmountKnown
    ? `$${surchargeAmountLabel}`
    : `${surchargePercentageLabel}%`;

  /* The seller's policy copy is rich text authored in the editor. It is not
     rendered inline under the checkbox - the checkbox is followed by a
     "View ..." link that opens the copy in a popup exactly as authored. The
     plain-text field is the fallback for sellers whose copy was saved without
     editor content, and a policy with neither has no link at all. */
  const getPolicyCopy = (
    rawContent?: string,
    plainText?: string,
  ): SellerPolicyCopy | null => {
    const html = rawContent
      ? UtilityMethods.getEditorHtmlContent(rawContent)
      : "";
    if (html) return { html, text: "" };
    if (plainText) return { html: "", text: plainText };
    return null;
  };

  /* A policy is shown whenever the checkout does not say "Do Not Display". The
     type only decides whether ticking its checkbox is required: "Require
     Acceptance" gates the pay button, "Display Only" leaves the box as an
     optional acknowledgement. */
  const showSellerTerms =
    payDetails.sellerTermsAndConditions !== "DoNotDisplay";

  const sellerPolicies: SellerPolicy[] = [
    {
      key: "terms",
      title: "Terms and Conditions",
      label: "I accept terms and conditions",
      linkCaption: "View terms and conditions",
      /* The purchase authorization paragraph is part of the terms the customer
         agrees to, so it is shown whether or not the seller has terms of their
         own. */
      displayed: true,
      description: (
        <CustomLabel>
          By purchasing today, you allow <strong>{seller?.name || ""}</strong>{" "}
          to charge your credit card <strong>${price}</strong> now and any
          future payments in accordance with their terms and conditions. You can
          cancel future payments on this credit card at any time. Changing your
          method of payment in the future may still result in an amount due
          charged for any active subscriptions unless cancelled.
        </CustomLabel>
      ),
      /* When the seller publishes their own Terms & Conditions the checkbox
         accepts those and the "View ..." link below opens them. Otherwise it is
         the generic purchase authorization checkbox, with nothing to view. */
      inputId: showSellerTerms ? "sellerTerms" : "terms",
      field: showSellerTerms
        ? "acceptSellerTermsAndConditions"
        : "acceptTermsAndConditions",
      /* The seller's message is only ever raised for "Require Acceptance" -
         validate() raises it for no other type. */
      errorMessage: showSellerTerms
        ? "*Please accept the seller's terms & conditions"
        : "*Please accept terms and conditions",
      copy: showSellerTerms
        ? getPolicyCopy(
            seller?.termsAndConditionsEditorRawContent,
            seller?.termsAndConditions,
          )
        : null,
    },
    {
      key: "returnPolicy",
      inputId: "returnPolicy",
      field: "acceptReturnPolicy",
      title: "Return Policy",
      label: "I accept return policy",
      linkCaption: "View return policy",
      errorMessage: "*Please accept the return policy",
      displayed: payDetails.sellerReturnPolicy !== "DoNotDisplay",
      copy: getPolicyCopy(
        seller?.returnPolicyEditorRawContent,
        seller?.returnPolicy,
      ),
    },
  ];

  const openPolicy = sellerPolicies.find(
    (policy) => policy.key === openPolicyKey,
  );

  /* The surcharge note is the last thing the customer reads before paying, so
     it sits directly above the pay button as a sibling of the terms blocks.
     That keeps its spacing on the form column's own gap, identical however
     many policy checkboxes happen to be on screen. */
  const renderSurchargeNote = () => {
    if (
      !payDetails.surchargeEnabled ||
      payDetails.surchargeAskCustomerFirst ||
      !includeSurcharge ||
      !includeSurchargeOnApplyPromo
    ) {
      return null;
    }
    return (
      <div>
        <CustomLabel className="surcharge-fee-text">
          A fee of <strong>{surchargeIncludedValue}</strong> has been added to
          cover processing fees.
        </CustomLabel>
      </div>
    );
  };

  const renderSellerPolicy = (policy: SellerPolicy) => {
    if (!policy.displayed) return null;
    return (
      <div key={policy.key}>
        <FlexContainer fxLayout="column" spacing={2}>
          <Input
            type="checkbox"
            id={policy.inputId}
            value={!!paymentSectionData[policy.field]}
            onChange={(e) => handleDataChange(policy.field, e.target.checked)}
            label={policy.label}
            className="terms-checkbox"
            error={paymentSectionData.hasError(policy.field)}
            errorMessage={policy.errorMessage}
          />
          {policy.description}
          {policy.copy && (
            <CustomLabel
              className="view-policy-link"
              onClick={() => setOpenPolicyKey(policy.key)}
            >
              {policy.linkCaption}
            </CustomLabel>
          )}
        </FlexContainer>
      </div>
    );
  };

  const renderPolicyPopup = () => {
    if (!openPolicy?.copy) return null;
    const { title, copy } = openPolicy;
    return (
      <ModalComponent
        title={title}
        titleFontSize="22px"
        bodyPadding="24px 30px"
        wrapperWidth="650px"
        minHeight="150px"
        /* Subtracted from 100vh by ModalBody, so long policies scroll inside
           the popup instead of pushing it past the viewport. */
        maxHeight="200px"
        bodyBackgroundColor="#fff"
        onClose={() => setOpenPolicyKey(null)}
      >
        {copy.html ? (
          <div
            className="policy-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(copy.html) }}
          />
        ) : (
          <div className="policy-content">{copy.text}</div>
        )}
      </ModalComponent>
    );
  };

  const renderPayButton = () => {
    const value =
      payDetails.formData.payButtonType === CheckOutPayButtonCaption.Custom
        ? payDetails.formData.buttonValue
        : CheckOutPayButtonCaption[payDetails.formData.payButtonType];

    const isButtonDisabled =
      merchantAccount.isFiservMerchantAccount() &&
      paymentSectionData.paymentMethodId === "";

    const handlePayClick = () => {
      const isValid = paymentSectionData.validate();

      if (!isValid) {
        handleDataChange("formData", { ...formData });
        return;
      }
      onPayClick();
    };

    return (
      <CustomButton
        id="pay-button"
        className="pay-button payment-main-btn"
        disabled={isButtonDisabled || isPaymentInProgress}
        bgColor={color?.primary?.rgba}
        pillShape={color?.buttonStyleType === CheckOutButtonStyle.Round}
        roundedRect={
          color?.buttonStyleType === CheckOutButtonStyle.SquareRoundCorner
        }
        onClick={() => handlePayClick()}
        ref={payButtonRef}
      >
        {price === AccountSettingsStore.formatAmount(0, true)
          ? "Place Order"
          : `${value} $${price}`}{" "}
      </CustomButton>
    );
  };

  const handleAddressChange = (
    addressType: keyof PurchasePaymentModel,
    field: keyof AddressModel,
    value: string | CountryWithStateModel | StateModel,
  ) => {
    if (addressType === "billingAddress" || addressType === "shippingAddress") {
      const updatedAddress = new AddressModel({
        ...paymentSectionData[addressType],
        [field]: value,
      });
      handleDataChange(addressType, updatedAddress);
    }
  };

  const handleShipping = (key: keyof PurchasePaymentModel, value: boolean) => {
    handleDataChange(key, value);
  };

  const checkBoxInput = (
    key: keyof PurchasePaymentModel,
    value: boolean,
    label: string,
    className?: string,
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleShipping(key, e.target.checked)}
        label={label}
        className={className}
      />
    );
  };
  return (
    <FlexContainer
      fxLayout="column"
      spacing={15}
      className={`payment-form-input ${hasMultiplePaymentMethods ? "multiple-payment-method-form" : "default-payment-method-form"} `}
    >
      {formFields.map((field: CheckOutPayPageFormFields) => renderField(field))}
      {/* {payDetails.allowTaxId && (
        <>
          <Input
            type="text"
            id="taxId"
            value={paymentSectionData.taxId}
            onChange={(e) => handleDataChange("taxId", e.target.value)}
            placeholder="Enter Tax Id"
            error={paymentSectionData.hasError(
              "taxId" as keyof PurchasePaymentModel
            )}
            errorMessage="*Please enter a valid tax id"
          />
        </>
      )} */}
      {payDetails.formData.isBillingAddressChecked && (
        <div ref={billingAddressRef} id="billingAddressSection">
          <FlexContainer fxLayout="column" spacing={15}>
            <CustomLabel className="sub-heading">Billing Address</CustomLabel>
            <AddressFormComponent
              address={paymentSectionData.billingAddress}
              countriesWithState={countriesWithState}
              onAddressChange={(
                field: keyof AddressModel,
                value: string | CountryWithStateModel | StateModel,
              ) => handleAddressChange("billingAddress", field, value)}
            />
          </FlexContainer>
        </div>
      )}
      {payDetails.formData.isShippingAddressChecked && (
        <div ref={shippingAddressRef} id="shippingAddressSection">
          <FlexContainer fxLayout="column" spacing={15}>
            <div className="full-width">
              <FlexContainer alignItems="center" justifyContent="space-between">
                <CustomLabel className="sub-heading" margin="10px 0px 0px">
                  Shipping Address
                </CustomLabel>
                {payDetails.formData.isBillingAddressChecked &&
                  checkBoxInput(
                    "shippingSameAsBilling",
                    paymentSectionData.shippingSameAsBilling,
                    "Same as Billing",
                    "same-as-billing",
                  )}
              </FlexContainer>
            </div>
            {!paymentSectionData.shippingSameAsBilling && (
              <AddressFormComponent
                address={paymentSectionData.shippingAddress}
                countriesWithState={countriesWithState}
                onAddressChange={(
                  field: keyof AddressModel,
                  value: string | CountryWithStateModel | StateModel,
                ) => handleAddressChange("shippingAddress", field, value)}
              />
            )}
          </FlexContainer>
        </div>
      )}
      <div>
        <CustomLabel
          className="sub-heading"
          margin={
            !paymentSectionData.shippingSameAsBilling
              ? "20px 0px 0px"
              : "10px 0px 0px"
          }
        >
          Terms & Conditions
        </CustomLabel>
      </div>
      {showSurchargeCheckbox && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="surcharge"
              value={paymentSectionData.surchargeChecked}
              onChange={(e) => {
                handleDataChange("surchargeChecked", e.target.checked);
              }}
              label="Cover Processing Fees"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "surchargeChecked" as keyof PurchasePaymentModel,
              )}
              errorMessage="*Please click this checkbox to cover processing fees"
            />
            <CustomLabel>{buildSurchargeConsentText()}</CustomLabel>
          </FlexContainer>
        </div>
      )}
      {false && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="updates"
              onChange={(e) => {
                handleDataChange("updatesOffers", e.target.checked);
              }}
              value={paymentSectionData.updatesOffers}
              label="Get updates and special offers"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "updatesOffers" as keyof PurchasePaymentModel,
              )}
              errorMessage="*Please select updates and special offers"
            />
            <CustomLabel>
              I agree to receive text and/or email communication. I may opt-out
              at any time. For text messages, message and data rates may apply
              and I may opt-out by replying STOP if texted.
            </CustomLabel>
          </FlexContainer>
        </div>
      )}
      {false && (
        <div>
          <FlexContainer fxLayout="column" spacing={2}>
            <Input
              type="checkbox"
              id="savePayment"
              value={paymentSectionData.savePaymentMethod}
              onChange={(e) => {
                handleDataChange("savePaymentMethod", e.target.checked);
              }}
              label="Save payment method for future purchases"
              className="terms-checkbox"
              error={paymentSectionData.hasError(
                "savePaymentMethod" as keyof PurchasePaymentModel,
              )}
              errorMessage="*Please select save payment methods"
            />
            <CustomLabel>
              We will send a text code to your mobile phone to confirm.
            </CustomLabel>
          </FlexContainer>
        </div>
      )}
      {/* The terms block, always shown, plus the seller's Return Policy when
          the checkout was built to display it. Either policy only gates the pay
          button when it was built to require acceptance. */}
      {sellerPolicies.map(renderSellerPolicy)}
      {renderPolicyPopup()}
      {renderSurchargeNote()}
      {renderPayButton()}
    </FlexContainer>
  );
};

export default observer(PurchasePaymentFormInputs);
